import { useState, useEffect } from "react";
import { assetsService } from "../services/assetsService";

/**
 * Azure Best Practice: Enhanced background image hook with configurable overlay and offline caching
 * @param url The URL of the background image
 * @param overlayType Type of overlay effect to apply
 * @param overlayOpacity Opacity level for the overlay (0-1)
 * @returns Object containing loading status and style
 */
export default function useBackgroundImage(
  url: string | undefined,
  overlayType:
    | "dark"
    | "light"
    | "blur"
    | "gradient"
    | "light-gradient"
    | "none" = "dark",
  overlayOpacity: number = 0.7,
) {
  const [fullUrl, setFullUrl] = useState<string | null>(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);

  useEffect(() => {
    if (!url) {
      setFullUrl(null);
      setImageLoaded(false);
      setImageError(false);
      return;
    }

    const loadUrl = async () => {
      try {
        const constructedUrl = await assetsService.getAsset(url, "background");
        setFullUrl(constructedUrl);
        setImageError(false);
      } catch (err) {
        setImageError(true);
        console.error(
          `useBackgroundImage: Error constructing URL: ${url}`,
          err,
        );
      }
    };

    loadUrl();
  }, [url]);

  // Azure Best Practice: Use viewport units for reliable container sizing
  const containerStyle =
    (url || fullUrl) && !imageError
      ? {
          position: "relative" as const,
          width: "100vw", // Use viewport width instead of percentage
          minHeight: "100vh", // Ensure minimum viewport height
          height: "100vh", // Use viewport height
          overflow: "hidden",
          color: overlayType === "light" ? "#333" : "#fff",
          display: "flex", // Use flexbox for better content distribution
          flexDirection: "column" as const,
        }
      : {};

  // Also update backgroundStyle for better coverage:
  const backgroundStyle =
    (url || fullUrl) && !imageError
      ? {
          position: "absolute" as const,
          top: 0,
          left: 0,
          right: 0, // Add right position
          bottom: 0, // Add bottom position
          width: "100%",
          height: "100%",
          zIndex: 0,
          backgroundImage: `url("${fullUrl || url}")`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          filter: overlayType === "blur" ? "blur(8px)" : undefined,
        }
      : {};

  // Azure Best Practice: Create overlay style based on selected type
  const overlayStyle =
    (url || fullUrl) && !imageError
      ? {
          position: "absolute" as const,
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          zIndex: 1,
          ...(overlayType === "dark" && {
            backgroundColor: `rgba(0, 0, 0, ${overlayOpacity})`,
          }),
          ...(overlayType === "light" && {
            backgroundColor: `rgba(255, 255, 255, ${overlayOpacity})`,
          }),
          ...(overlayType === "gradient" && {
            backgroundImage: `linear-gradient(to bottom, 
    rgba(40, 40, 40, ${Math.min(overlayOpacity + 0.2, 0.95)}) 0%, 
    rgba(40, 40, 40, ${overlayOpacity}) 40%, 
    rgba(40, 40, 40, ${overlayOpacity}) 100%)`,
          }),
          ...(overlayType === "light-gradient" && {
            backgroundImage: `linear-gradient(to bottom, 
        rgba(255, 255, 255, ${Math.min(overlayOpacity + 0.1, 0.95)}) 0%, 
        rgba(255, 255, 255, ${overlayOpacity}) 40%, 
        rgba(255, 255, 255, ${overlayOpacity}) 100%)`,
          }),
          ...(overlayType === "none" && {
            display: "none",
          }),
        }
      : {};

  // Azure Best Practice: Style for content to appear above overlay
  const contentStyle =
    (url || fullUrl) && !imageError
      ? {
          position: "relative" as const,
          zIndex: 2,
          height: "100%",
        }
      : {};

  return {
    isLoading: (url || fullUrl) && !imageLoaded && !imageError,
    hasError: imageError,
    containerStyle,
    backgroundStyle,
    overlayStyle,
    contentStyle,
  };
}
